import { useEffect, useState } from 'react';
import { getLearnerTranscript, getLearnerTranscriptBinary } from '../../../../util/APIUtils';
import { TranscriptConstants } from '@orijinworks/frontend-commons';
import { convertBase64ToBlob, downloadBlobFile } from '../../../services/utilty';
import store, { showToast } from '../../../core/store';
import useUnleashHook from '../../../../v2/core/unleash/useUnleashHook';
import { UnleashService } from '../../../../v2/services';

import {
  CONTENT_PROVIDER_D2L,
  CONTENT_PROVIDER_D2L_LABELS
} from '../../../services/constants';

export const useTranscriptHook = (props) => {
  const { user } = props;
  const [transcriptData, setTranscriptData] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const showAwardsInTranscript = useUnleashHook(UnleashService.FLAGS.SHOW_AWARDS_IN_TRANSCRIPT).isFlagEnabled();
  const showStudyTimeColumn = useUnleashHook(UnleashService.FLAGS.SHOW_STUDY_TIME_IN_TRANSCRIPT).isFlagEnabled();
  useEffect(() => {
    fetchTranscriptData(parseInt(user.userDetail.userId));
  }, []);

  const onVersionChange = () => {
    setIsDisabled(false);
  };

   /**
   * Transforms transcript response data by renaming and reassigning time tracking fields.
   *
   * Specifically:
   * - Replaces `totalTimeSpentMinutes` in each `program` with `timeSpentMinutes`.
   * - Replaces `timeSpentMinutes` in each `learningObject` with `totalTimeSpentMinutes`.
   * - Filters out any `content` objects that have an empty or missing `programs` array.
   *
   * This transformation ensures consistency with expected property names before passing
   * the data to the TranscriptV2 component or other consumers.
   *
   * @param {Object} transcriptResponse The original transcript response object.
   * @returns {Object} The transformed transcript object with updated time field names.
   */
   const transformTranscriptData = (transcriptResponse) => {
    if (!transcriptResponse.contents) return transcriptResponse;

    return {
      ...transcriptResponse,
      contents: transcriptResponse.contents
        .filter((content) => Array.isArray(content.programs) && content.programs.length > 0)
        .map((content) => ({
          ...content,
          programs: content.programs.map(({ totalTimeSpentMinutes, learningObjects = [], ...restProgram }) => ({
            ...restProgram,
            timeSpentMinutes: totalTimeSpentMinutes,
            learningObjects: learningObjects.map(({ timeSpentMinutes, ...restLo }) => ({
              ...restLo,
              totalTimeSpentMinutes: timeSpentMinutes,
            })),
          })),
          isProgramAwardable: content.provider?.name === CONTENT_PROVIDER_D2L_LABELS[CONTENT_PROVIDER_D2L.INTERPLAY]
        })),
    };
  };

  const doesTranscriptHaveContents = (data) => {
    let flag = false;
    if (data.length > 0 && data.filter((item) => item.programs.length > 0).length > 0) {
      flag = true;
    }
    return flag;
  };

  const fetchTranscriptData = async (userId) => {
    try {
      const response = await getLearnerTranscript(userId);
      if (response.payload) {
        if (!doesTranscriptHaveContents(response.payload.contents)) {
          setIsDisabled(true);
        }
        setTranscriptData(transformTranscriptData(response.payload));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadV1Transcript = () => {
    try {
      document.querySelector('#legacy-transcript-download-btn').click();
    } catch (e) {
      console.error(e);
    }
  };

  const downloadV2Transcript = async (userId) => {
    setIsLoading(true);
    const fileName = `Transcript_${user.userDetail.userName}`;
    try {
      const { payload } = await getLearnerTranscriptBinary(userId);
      downloadBlobFile(convertBase64ToBlob(payload, 'application/pdf'), fileName);
    } catch (e) {
      store.dispatch(showToast(`Something went wrong while fetching certificate.`));
      console.error('Error downloading transcript', e);
    }
    setIsLoading(false);
  };

  const onDownloadClick = (version) => {
    switch (version) {
      case TranscriptConstants.V1:
        downloadV1Transcript();
        break;
      default:
        downloadV2Transcript(parseInt(user.userDetail.userId));
    }
  };
  return { transcriptData, isLoading, isDisabled, setIsDisabled, onDownloadClick, onVersionChange, showAwardsInTranscript, showStudyTimeColumn };
};
